/**
 * InactivePatientsList Component
 *
 * Displays a list of patients who have not been seen in 90+ days.
 * Read-only component - no data modification allowed.
 *
 * @example
 * ```tsx
 * <InactivePatientsList
 *   patients={inactivePatients}
 *   thresholdDays={90}
 *   loading={false}
 * />
 * ```
 */

import React from 'react';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { InactivePatient } from '../../../services/clinicalDashboardService';
import { parseApiDate } from '../../../lib/format';

interface InactivePatientsListProps {
  /** List of inactive patients */
  patients: InactivePatient[];
  /** Number of days threshold for inactivity */
  thresholdDays: number;
  /** Loading state */
  loading: boolean;
  /** Optional CSS class */
  className?: string;
}

/**
 * Formats a date string to local Spanish format
 */
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return (parseApiDate(dateStr) ?? new Date(NaN)).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Returns appropriate badge class based on days inactive
 */
const getDaysBadgeClass = (days: number | null): string => {
  if (days === null) return 'bg-secondary';
  if (days >= 120) return 'bg-danger';
  if (days >= 90) return 'bg-warning';
  return 'bg-info';
};

/**
 * Returns label text based on days inactive
 */
const getDaysLabel = (days: number | null): string => {
  if (days === null) return 'Sin visitas';
  if (days === 1) return '1 día';
  return `${days} días`;
};

const InactivePatientsList: React.FC<InactivePatientsListProps> = ({
  patients,
  thresholdDays,
  loading,
  className = '',
}) => {
  return (
    <div className={`card ${className}`}>
      <div className="card-header">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h4 className="card-title d-inline-block me-2">
              Pacientes Inactivos
            </h4>
            <span className="badge bg-warning text-dark">
              {thresholdDays}+ días sin visita
            </span>
          </div>
          <span className="text-muted">
            <FeatherIcon icon="lock" className="me-1" style={{ width: 14, height: 14 }} />
            Solo lectura
          </span>
        </div>
      </div>

      <div className="card-body p-0">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="text-muted mt-2 mb-0">Cargando pacientes...</p>
          </div>
        ) : patients.length === 0 ? (
          <div className="text-center py-5">
            <FeatherIcon
              icon="check-circle"
              className="text-success mb-2"
              style={{ width: 48, height: 48, opacity: 0.3 }}
            />
            <p className="text-muted mb-0">Sin pacientes inactivos</p>
            <small className="text-muted">Todos los pacientes han sido vistos recentemente</small>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Paciente</th>
                  <th>MRN</th>
                  <th>Última Visita</th>
                  <th>Días Inactivo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((patient) => (
                  <tr key={patient.id}>
                    <td>
                      <Link to={`/patients/${patient.id}`} className="text-primary fw-medium">
                        {patient.first_name} {patient.last_name}
                      </Link>
                    </td>
                    <td className="text-muted">
                      {patient.medical_record_number || '-'}
                    </td>
                    <td>{formatDate(patient.last_consultation_date)}</td>
                    <td>
                      <span className={`badge ${getDaysBadgeClass(patient.days_since_last_visit)}`}>
                        {getDaysLabel(patient.days_since_last_visit)}
                      </span>
                    </td>
                    <td>
                      <Link
                        to={`/patients/${patient.id}`}
                        className="btn btn-sm btn-outline-primary"
                        title="Ver paciente"
                      >
                        <FeatherIcon icon="eye" style={{ width: 14, height: 14 }} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {patients.length > 0 && (
        <div className="card-footer text-muted">
          <small>
            Mostrando {patients.length} paciente{patients.length !== 1 ? 's' : ''} inactivo
            {patients.length !== 1 ? 's' : ''}
          </small>
        </div>
      )}
    </div>
  );
};

export default InactivePatientsList;